import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Activity,
  CheckCircle,
  Clock,
  FileText,
  Heart,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  XCircle,
  Inbox,
  Settings,
  ClipboardList,
  Bot,
  Wifi,
  WifiOff,
  Mail,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  File,
  FolderOpen,
  Edit3,
  Save,
  X,
  ArrowRight,
  Loader2,
  Download,
  Trash2,
} from "lucide-react";
import { API_URL } from "../config/api";
import { Navbar } from "../components/Navbar";

const RISK_COLORS = {
  high: "bg-red-100 text-red-800 border-red-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-green-100 text-green-800 border-green-200",
};

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  pending_approval: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  completed: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  blocked: "bg-red-100 text-red-800",
  awaiting_data: "bg-purple-100 text-purple-800",
  changes_requested: "bg-orange-100 text-orange-800",
};

const INTENT_LABELS = {
  create_contract: "Pogodba",
  create_annex: "Aneks",
  prepare_termination: "Odpoved",
  send_reminder: "Opomnik",
  compliance_check: "Skladnost",
  generate_rulebook: "Pravilnik",
  leave_notice: "Dopust",
  summarize_thread: "Povzetek",
  unknown: "Neznano",
};

const DOC_TYPE_LABELS = {
  contract: "Pogodba",
  annex: "Aneks",
  notice: "Obvestilo",
  reminder: "Opomnik",
  rulebook: "Pravilnik",
  other: "Drugo",
};

const DOC_STATUS_LABELS = {
  draft: "Osnutek",
  pending_review: "V pregledu",
  approved: "Odobreno",
  sent: "Poslano",
  archived: "Arhivirano",
};

const DOC_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  sent: "bg-blue-100 text-blue-700",
  archived: "bg-gray-100 text-gray-500",
};

export default function HRBotPage() {
  const [token] = useState(() => localStorage.getItem("token"));
  const [dashboard, setDashboard] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const deleteDocument = async (docId, title) => {
    if (!window.confirm(`Izbrisati osnutek "${title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/hrbot/documents/${docId}`, { headers });
      toast.success(`Izbrisano: ${title}`);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka pri brisanju dokumenta");
    }
  };

  const downloadDocument = async (docId, title, format = "docx") => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/documents/${docId}/download?format=${format}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (title || docId).replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "_");
      a.download = `${safeName}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Preneseno: ${safeName}.${format}`);
    } catch (err) {
      toast.error("Napaka pri prenosu dokumenta");
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  }, []);

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/approvals`, { headers });
      setApprovals(res.data.approvals || []);
    } catch (err) {
      console.error("Approvals fetch error:", err);
    }
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/cases?limit=100`, { headers });
      setAllCases(res.data.cases || []);
    } catch (err) {
      console.error("Cases fetch error:", err);
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/audit?limit=50`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error("Audit fetch error:", err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/settings`, { headers });
      setSettings(res.data);
    } catch (err) {
      console.error("Settings fetch error:", err);
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/documents?limit=100`, { headers });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error("Documents fetch error:", err);
    }
  }, []);

  const fetchCaseDetail = async (caseId) => {
    try {
      const res = await axios.get(`${API_URL}/hrbot/cases/${caseId}`, { headers });
      setSelectedCase(res.data);
    } catch (err) {
      toast.error("Napaka pri nalaganju primera");
    }
  };

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchApprovals(), fetchCases(), fetchAudit(), fetchSettings(), fetchDocuments()]);
    setLoading(false);
  }, [fetchDashboard, fetchApprovals, fetchCases, fetchAudit, fetchSettings, fetchDocuments]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const handleApproval = async (caseId, action) => {
    try {
      await axios.post(`${API_URL}/hrbot/approvals/${caseId}`, {
        action,
        approved_by: "admin",
        notes: action === "approve" ? "Odobreno" : "Zavrnjeno",
      }, { headers });
      toast.success(`Primer ${caseId}: ${action === "approve" ? "odobren" : "zavrnjen"}`);
      refreshAll();
      if (selectedCase?.case_id === caseId) fetchCaseDetail(caseId);
    } catch (err) {
      toast.error(`Napaka: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const payload = {};
      if (settingsForm.monitored_mailboxes !== undefined) {
        payload.monitored_mailboxes = settingsForm.monitored_mailboxes.split(",").map(s => s.trim()).filter(Boolean);
      }
      if (settingsForm.trigger_words !== undefined) {
        payload.trigger_words = settingsForm.trigger_words.split(",").map(s => s.trim()).filter(Boolean);
      }
      if (settingsForm.heartbeat_alert_email !== undefined) {
        payload.heartbeat_alert_email = settingsForm.heartbeat_alert_email;
      }
      if (settingsForm.escalation_emails !== undefined) {
        payload.escalation_emails = settingsForm.escalation_emails.split(",").map(s => s.trim()).filter(Boolean);
      }
      if (settingsForm.auto_actions_enabled !== undefined) {
        payload.auto_actions_enabled = settingsForm.auto_actions_enabled;
      }
      await axios.put(`${API_URL}/hrbot/settings`, payload, { headers });
      toast.success("Nastavitve shranjene");
      setEditingSettings(false);
      fetchSettings();
    } catch (err) {
      toast.error("Napaka pri shranjevanju nastavitev");
    }
  };

  const stats = dashboard?.stats || {};
  const agents = dashboard?.agents || [];

  // Inbox: cases with status "new"
  const inboxCases = allCases.filter(c => c.status === "new");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hrbot-page">
      <Navbar />
      <main className="container mx-auto px-4 py-6 pt-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="hrbot-title">
              <Bot className="h-7 w-7 text-indigo-600" />
              HRBot Nadzorna Plosca
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              HR Agent Monitor | Email Commands | Dokumenti | Odobritve | Revizija
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Osvezi
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Agenti" value={agents.length || 0} icon={agents.some(a => a.is_healthy) ? Wifi : WifiOff}
            iconColor={agents.some(a => a.is_healthy) ? "text-green-600" : "text-slate-400"}
            bgColor={agents.some(a => a.is_healthy) ? "bg-green-100" : "bg-slate-100"} testId="stat-agents" />
          <StatCard label="Odprti primeri" value={stats.in_progress || 0} icon={Inbox} iconColor="text-blue-600" bgColor="bg-blue-100" testId="stat-cases" />
          <StatCard label="Cakajo odobritev" value={stats.pending_approvals || 0} icon={Clock} iconColor="text-amber-600" bgColor="bg-amber-100" testId="stat-approvals" highlight />
          <StatCard label="Visoko tveganje" value={stats.high_risk_open || 0} icon={ShieldAlert} iconColor="text-red-600" bgColor="bg-red-100" testId="stat-risk" highlight="red" />
          <StatCard label="Dokumenti" value={stats.total_documents || 0} icon={FileText} iconColor="text-indigo-600" bgColor="bg-indigo-100" testId="stat-docs" />
          <StatCard label="Zakljuceni" value={stats.completed || 0} icon={CheckCircle} iconColor="text-emerald-600" bgColor="bg-emerald-100" testId="stat-completed" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-white border flex-wrap">
            <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
              <Activity className="h-4 w-4" /> Pregled
            </TabsTrigger>
            <TabsTrigger value="inbox" className="gap-2" data-testid="tab-inbox">
              <Mail className="h-4 w-4" /> Prejeto
              {inboxCases.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">{inboxCases.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="cases" className="gap-2" data-testid="tab-cases">
              <Inbox className="h-4 w-4" /> Primeri
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2" data-testid="tab-documents">
              <FolderOpen className="h-4 w-4" /> Dokumenti
              {(stats.draft_documents || 0) > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-500 text-white rounded-full">{stats.draft_documents}</span>}
            </TabsTrigger>
            <TabsTrigger value="approvals" className="gap-2" data-testid="tab-approvals">
              <ShieldCheck className="h-4 w-4" /> Odobritve
              {(stats.pending_approvals || 0) > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">{stats.pending_approvals}</span>}
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit">
              <ClipboardList className="h-4 w-4" /> Revizija
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Settings className="h-4 w-4" /> Nastavitve
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            <AgentMonitor agents={agents} headers={headers} />
            <RecentCases cases={dashboard?.recent_cases || []} onSelectCase={fetchCaseDetail} />
          </TabsContent>

          {/* INBOX TAB - Email Command Inbox */}
          <TabsContent value="inbox" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" /> Prejeti @hrbot ukazi ({inboxCases.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {inboxCases.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Ni novih @hrbot ukazov.</p>
                    <p className="text-xs mt-1">Email ukazi bodo prikazani tukaj ko MyClaw agent poslje nov primer.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inboxCases.map((c, i) => (
                      <InboxItem key={i} c={c} onSelect={() => fetchCaseDetail(c.case_id)} onApprove={handleApproval} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CASES TAB */}
          <TabsContent value="cases" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Vsi primeri ({allCases.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {allCases.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Se ni primerov.</p>
                ) : (
                  <div className="space-y-2">
                    {allCases.map((c, i) => (
                      <CaseRow key={i} c={c} onClick={() => fetchCaseDetail(c.case_id)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-indigo-600" /> Vrsta dokumentov ({documents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Ni dokumentov.</p>
                    <p className="text-xs mt-1">Dokumenti generirani od MyClaw agenta bodo prikazani tukaj.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc, i) => (
                      <DocumentRow key={i} doc={doc} onSelectCase={() => fetchCaseDetail(doc.case_id)} onDownload={downloadDocument} onDelete={deleteDocument} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* APPROVALS TAB */}
          <TabsContent value="approvals" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-600" /> Cakajo odobritev ({approvals.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {approvals.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Ni odprtih odobritev.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {approvals.map((c, i) => (
                      <ApprovalCard key={i} c={c} onApproval={handleApproval} onSelect={() => fetchCaseDetail(c.case_id)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUDIT TAB */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-slate-600" /> Revizijska sled
                </CardTitle>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Revizijska sled je prazna.</p>
                ) : (
                  <div className="space-y-1">
                    {auditLogs.map((log, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
                        <span className="text-xs text-slate-400 w-36 flex-shrink-0">{new Date(log.timestamp).toLocaleString("sl-SI")}</span>
                        <span className="font-mono text-xs text-indigo-600 w-24 flex-shrink-0">{log.action}</span>
                        {log.case_id && <span className="font-mono text-xs text-slate-500 w-24 flex-shrink-0">{log.case_id}</span>}
                        <span className="text-slate-600 truncate">{log.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-4">
            <SettingsPanel
              settings={settings}
              editing={editingSettings}
              form={settingsForm}
              onEdit={() => {
                setSettingsForm({
                  monitored_mailboxes: settings?.monitored_mailboxes?.join(", ") || "",
                  trigger_words: settings?.trigger_words?.join(", ") || "",
                  heartbeat_alert_email: settings?.heartbeat_alert_email || "",
                  escalation_emails: settings?.escalation_emails?.join(", ") || "",
                  auto_actions_enabled: settings?.auto_actions_enabled ?? true,
                });
                setEditingSettings(true);
              }}
              onCancel={() => setEditingSettings(false)}
              onSave={handleSaveSettings}
              onChange={(field, value) => setSettingsForm(prev => ({ ...prev, [field]: value }))}
            />
            <LegalSourcesCard />
          </TabsContent>
        </Tabs>

        {/* Case Detail Modal */}
        {selectedCase && (
          <CaseDetailModal
            caseData={selectedCase}
            onClose={() => setSelectedCase(null)}
            onApproval={handleApproval}
            onDownload={downloadDocument}
          />
        )}
      </main>
    </div>
  );
}

// ─── Sub-components ───

function StatCard({ label, value, icon: Icon, iconColor, bgColor, testId, highlight }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${highlight === "red" ? "text-red-600" : highlight ? "text-amber-600" : ""}`}>{value}</p>
          </div>
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AgentMonitor({ agents, headers }) {
  const [sendingTest, setSendingTest] = useState(false);

  const handleTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API_URL}/hrbot/heartbeat/test-email`, {}, { headers });
      if (res.data.email_sent) {
        toast.success("Testni email poslan!");
      } else {
        toast.error(`Email ni bil poslan: ${res.data.error || "Neznana napaka"}`);
      }
    } catch (err) {
      toast.error("Napaka pri posiljanju testnega emaila");
    }
    setSendingTest(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="h-4 w-4 text-red-500" /> Agent Monitor
          </CardTitle>
          <Button size="sm" variant="outline" className="gap-1" onClick={handleTestEmail} disabled={sendingTest} data-testid="test-email-btn">
            {sendingTest ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
            Posli testni email
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {agents.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Bot className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Se ni registriranih agentov.</p>
            <p className="text-xs mt-1">MyClaw agent bo tukaj prikazan po prvem heartbeat-u.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agents.map((agent, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${agent.is_healthy ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${agent.is_healthy ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
                  <div>
                    <p className="font-medium text-sm">{agent.agent}</p>
                    <p className="text-xs text-slate-500">
                      Zadnji heartbeat: {agent.last_heartbeat ? new Date(agent.last_heartbeat).toLocaleString("sl-SI") : "N/A"}
                      {agent.hours_since != null && ` (pred ${agent.hours_since}h)`}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${agent.is_healthy ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {agent.status || (agent.is_healthy ? "ONLINE" : "OFFLINE")}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentCases({ cases, onSelectCase }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-600" /> Nedavni primeri
        </CardTitle>
      </CardHeader>
      <CardContent>
        {cases.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Se ni primerov. @hrbot ukazi bodo prikazani tukaj.</p>
        ) : (
          <div className="space-y-2">
            {cases.map((c, i) => (
              <CaseRow key={i} c={c} onClick={() => onSelectCase(c.case_id)} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CaseRow({ c, onClick }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors cursor-pointer"
      data-testid={`case-${c.case_id}`}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-xs text-slate-400">{c.case_id}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>
            {c.status?.replace(/_/g, " ")}
          </span>
          <span className={`px-1.5 py-0.5 rounded text-xs border ${RISK_COLORS[c.risk_level] || ""}`}>
            {c.risk_level}
          </span>
          {c.escalated && <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">ESKALIRAN</span>}
        </div>
        <p className="text-sm font-medium truncate">{c.subject}</p>
        <p className="text-xs text-slate-500">{c.from_email} | {INTENT_LABELS[c.intent] || c.intent}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(c.created_at).toLocaleDateString("sl-SI")}</span>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
    </div>
  );
}

function InboxItem({ c, onSelect, onApprove }) {
  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-500" />
          <span className="font-mono text-xs text-slate-500">{c.case_id}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs border ${RISK_COLORS[c.risk_level] || ""}`}>
            {c.risk_level?.toUpperCase()}
          </span>
          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
            {INTENT_LABELS[c.intent] || c.intent}
          </span>
        </div>
        <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString("sl-SI")}</span>
      </div>
      <p className="font-medium text-sm mb-1">{c.subject}</p>
      <p className="text-xs text-slate-500 mb-1">Od: {c.from_email}</p>
      {c.body_preview && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{c.body_preview}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={onSelect} data-testid={`inbox-view-${c.case_id}`}>
          <FileText className="h-3 w-3 mr-1" /> Odpri
        </Button>
        {c.risk_level === "low" && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(c.case_id, "approve")} data-testid={`inbox-approve-${c.case_id}`}>
            <CheckCircle className="h-3 w-3 mr-1" /> Avtom. odobri
          </Button>
        )}
      </div>
    </div>
  );
}

function DocumentRow({ doc, onSelectCase, onDownload, onDelete }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors" data-testid={`doc-${doc.doc_id}`}>
      <div className={`p-2 rounded-lg ${DOC_STATUS_COLORS[doc.status] || "bg-slate-100"}`}>
        <File className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-xs text-slate-400">{doc.doc_id}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs ${DOC_STATUS_COLORS[doc.status] || "bg-slate-100"}`}>
            {DOC_STATUS_LABELS[doc.status] || doc.status}
          </span>
          <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">
            {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
          </span>
        </div>
        <p className="text-sm font-medium truncate">{doc.title}</p>
        <p className="text-xs text-slate-500">
          Primer: <button onClick={onSelectCase} className="text-indigo-600 hover:underline">{doc.case_id}</button>
          {" | "}Ustvaril: {doc.generated_by || "—"}
          {" | "}{new Date(doc.created_at).toLocaleDateString("sl-SI")}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button size="sm" variant="outline" className="gap-1" onClick={() => onDownload(doc.doc_id, doc.title, "docx")} data-testid={`download-docx-${doc.doc_id}`}>
          <Download className="h-3 w-3" /> Word
        </Button>
        <Button size="sm" variant="ghost" className="gap-1 text-slate-500" onClick={() => onDownload(doc.doc_id, doc.title, "txt")} data-testid={`download-txt-${doc.doc_id}`}>
          <Download className="h-3 w-3" /> TXT
        </Button>
        {doc.status === "draft" && onDelete && (
          <Button size="sm" variant="ghost" className="gap-1 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onDelete(doc.doc_id, doc.title)} data-testid={`delete-doc-${doc.doc_id}`}>
            <Trash2 className="h-3 w-3" /> Izbrisi
          </Button>
        )}
      </div>
    </div>
  );
}

function ApprovalCard({ c, onApproval, onSelect }) {
  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-500">{c.case_id}</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${RISK_COLORS[c.risk_level] || ""}`}>
            {c.risk_level?.toUpperCase()}
          </span>
          {c.escalated && <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">ESKALIRAN</span>}
        </div>
        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
          {INTENT_LABELS[c.intent] || c.intent}
        </span>
      </div>
      <p className="font-medium text-sm">{c.subject}</p>
      <p className="text-xs text-slate-500 mt-1">Od: {c.from_email} | {new Date(c.created_at).toLocaleString("sl-SI")}</p>
      {c.approval_required?.length > 0 && (
        <p className="text-xs text-amber-600 mt-1">Potrebna odobritev: {c.approval_required.join(", ")}</p>
      )}
      <div className="flex gap-2 mt-3">
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApproval(c.case_id, "approve")} data-testid={`approve-${c.case_id}`}>
          <CheckCircle className="h-3 w-3 mr-1" /> Odobri
        </Button>
        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => onApproval(c.case_id, "reject")} data-testid={`reject-${c.case_id}`}>
          <XCircle className="h-3 w-3 mr-1" /> Zavrni
        </Button>
        <Button size="sm" variant="ghost" onClick={onSelect} data-testid={`detail-${c.case_id}`}>
          <FileText className="h-3 w-3 mr-1" /> Podrobnosti
        </Button>
      </div>
    </div>
  );
}

function CaseDetailModal({ caseData, onClose, onApproval, onDownload }) {
  const events = caseData.events || [];
  const docs = caseData.case_documents || [];
  const facts = caseData.facts || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="case-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold text-indigo-600">{caseData.case_id}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[caseData.status] || "bg-slate-100"}`}>
                {caseData.status?.replace(/_/g, " ")}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs border ${RISK_COLORS[caseData.risk_level] || ""}`}>
                {caseData.risk_level?.toUpperCase()}
              </span>
              {caseData.escalated && <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">ESKALIRAN</span>}
            </div>
            <p className="text-sm font-medium mt-1">{caseData.subject}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg" data-testid="close-case-detail">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6 py-4 space-y-5">
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500 text-xs">Od</span>
              <p className="font-medium">{caseData.from_email}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs">Namen</span>
              <p className="font-medium">{INTENT_LABELS[caseData.intent] || caseData.intent}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs">Ustvarjeno</span>
              <p>{new Date(caseData.created_at).toLocaleString("sl-SI")}</p>
            </div>
            <div>
              <span className="text-slate-500 text-xs">Posodobljeno</span>
              <p>{new Date(caseData.updated_at).toLocaleString("sl-SI")}</p>
            </div>
          </div>

          {caseData.body_preview && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Predogled emaila</p>
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border">{caseData.body_preview}</div>
            </div>
          )}

          {/* Facts */}
          {Object.keys(facts).length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Dejstva</p>
              <div className="bg-slate-50 rounded-lg p-3 border space-y-1">
                {Object.entries(facts).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approval Required */}
          {caseData.approval_required?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Potrebna odobritev</p>
              <div className="flex gap-2">
                {caseData.approval_required.map((role, i) => (
                  <span key={i} className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs border border-amber-200">{role}</span>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {docs.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Dokumenti ({docs.length})</p>
              <div className="space-y-2">
                {docs.map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg border bg-white">
                    <File className="h-4 w-4 text-indigo-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-slate-500">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type} | {DOC_STATUS_LABELS[doc.status] || doc.status}</p>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${DOC_STATUS_COLORS[doc.status] || ""}`}>
                      {DOC_STATUS_LABELS[doc.status] || doc.status}
                    </span>
                    {onDownload && (
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => onDownload(doc.doc_id, doc.title, "docx")}>
                        <Download className="h-3 w-3" /> Word
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Casovnica ({events.length})</p>
            <div className="space-y-0 border-l-2 border-slate-200 ml-2">
              {events.map((evt, i) => (
                <div key={i} className="flex items-start gap-3 pl-4 pb-3 relative">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${
                    evt.type === "created" ? "bg-blue-500" :
                    evt.type.startsWith("approval_approve") ? "bg-green-500" :
                    evt.type.startsWith("approval_reject") ? "bg-red-500" :
                    evt.type === "escalation" ? "bg-orange-500" :
                    evt.type === "decision" ? "bg-purple-500" :
                    evt.type === "document_added" ? "bg-indigo-500" :
                    "bg-slate-400"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-500">{evt.type}</span>
                      <span className="text-xs text-slate-400">{new Date(evt.timestamp).toLocaleString("sl-SI")}</span>
                    </div>
                    <p className="text-sm text-slate-700">{evt.detail}</p>
                    {evt.notes && <p className="text-xs text-slate-500 mt-0.5">{evt.notes}</p>}
                    {evt.approved_by && <p className="text-xs text-slate-500">Od: {evt.approved_by}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approvals History */}
          {caseData.approvals?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Odobritve</p>
              <div className="space-y-2">
                {caseData.approvals.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded border bg-white text-sm">
                    {a.action === "approve" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    <span className="font-medium">{a.action === "approve" ? "Odobreno" : "Zavrnjeno"}</span>
                    <span className="text-slate-500">od {a.by}</span>
                    <span className="text-xs text-slate-400 ml-auto">{new Date(a.at).toLocaleString("sl-SI")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {caseData.status === "pending_approval" && (
          <div className="px-6 py-3 border-t bg-slate-50 flex gap-2">
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => onApproval(caseData.case_id, "approve")} data-testid="modal-approve">
              <CheckCircle className="h-4 w-4 mr-2" /> Odobri
            </Button>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => onApproval(caseData.case_id, "reject")} data-testid="modal-reject">
              <XCircle className="h-4 w-4 mr-2" /> Zavrni
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsPanel({ settings, editing, form, onEdit, onCancel, onSave, onChange }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4 text-slate-600" /> Nastavitve HRBot
          </CardTitle>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="edit-settings-btn">
              <Edit3 className="h-3 w-3 mr-1" /> Uredi
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={onSave} data-testid="save-settings-btn">
                <Save className="h-3 w-3 mr-1" /> Shrani
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancel}>
                <X className="h-3 w-3 mr-1" /> Preklici
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {settings ? (
          <div className="space-y-4">
            {editing ? (
              <>
                <EditableField label="Nadzorovani postni nabiralniki" value={form.monitored_mailboxes}
                  onChange={v => onChange("monitored_mailboxes", v)} hint="Loci z vejico" />
                <EditableField label="Sprozilne besede" value={form.trigger_words}
                  onChange={v => onChange("trigger_words", v)} hint="Loci z vejico" />
                <EditableField label="Email za heartbeat opozorila" value={form.heartbeat_alert_email}
                  onChange={v => onChange("heartbeat_alert_email", v)} />
                <EditableField label="Email za eskalacijo" value={form.escalation_emails}
                  onChange={v => onChange("escalation_emails", v)} hint="Loci z vejico" />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-600">Avtomatska dejanja</span>
                  <button
                    onClick={() => onChange("auto_actions_enabled", !form.auto_actions_enabled)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${form.auto_actions_enabled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                    data-testid="toggle-auto-actions"
                  >
                    {form.auto_actions_enabled ? "Vklopljeno" : "Izklopljeno"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <SettingRow label="Nadzorovani postni nabiralniki" value={settings.monitored_mailboxes?.join(", ")} />
                <SettingRow label="Sprozilne besede" value={settings.trigger_words?.join(", ")} />
                <SettingRow label="Email za heartbeat opozorila" value={settings.heartbeat_alert_email} />
                <SettingRow label="Email za eskalacijo" value={settings.escalation_emails?.join(", ")} />
                <SettingRow label="Heartbeat alert prejemniki" value={settings.heartbeat_alert_recipients?.join(", ")} />
                <SettingRow label="Avtomatska dejanja" value={settings.auto_actions_enabled ? "Vklopljeno" : "Izklopljeno"} />
                <SettingRow label="Casovna omejitev eskalacije" value={`${settings.escalation_timeout_hours || 48}h`} />
                <SettingRow label="Nikoli avtomatski nameni" value={settings.never_auto_intents?.join(", ")} />
              </>
            )}
            {!editing && settings.risk_policy && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Politika tveganja</p>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(settings.risk_policy).map(([level, policy]) => (
                    <div key={level} className={`p-3 rounded-lg border ${RISK_COLORS[level]}`}>
                      <p className="font-medium text-xs uppercase">{level}</p>
                      <p className="text-xs mt-1">Odobritev: {policy.approval_required?.join(", ") || "ni potrebna"}</p>
                      <p className="text-xs">Avto: {policy.auto_allowed ? "Da" : "Ne"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!editing && settings.roles && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Vloge</p>
                <div className="flex flex-wrap gap-2">
                  {settings.roles.map((role, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">{role}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Nalaganje nastavitev...</p>
        )}
      </CardContent>
    </Card>
  );
}

function EditableField({ label, value, onChange, hint }) {
  return (
    <div className="space-y-1">
      <label className="text-sm text-slate-600">{label}</label>
      <Input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={label} className="text-sm" />
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function SettingRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm font-medium text-slate-800">{value || "—"}</span>
    </div>
  );
}

function LegalSourcesCard() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Pravni viri (Slovenija)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SourceLink label="PISRS - Veljavni predpisi" url="https://pisrs.si/" type="primary" />
          <SourceLink label="SPOT - Zaposlovanje" url="https://spot.gov.si/" type="primary" />
          <SourceLink label="IP-RS - Varstvo podatkov" url="https://www.ip-rs.si/" type="primary" />
          <SourceLink label="Kolektivna pogodba - spedicija" url="https://pisrs.si/pregledPredpisa?id=KOLP89" type="primary" />
          <SourceLink label="GOV.SI - Delovna razmerja" url="https://www.gov.si/teme/delovna-razmerja/" type="primary" />
          <SourceLink label="FinD-INFO - Izracun place" url="https://www.findinfo.si/Tools/Index?Type=SalaryCalculation" type="secondary" />
          <SourceLink label="Pravnik na dlani - Pravilniki" url="https://pravniknadlani.si/obvezni-interni-akti-in-pravilniki-za-podjetja/" type="secondary" />
          <SourceLink label="DATA - Kadrovske evidence" url="https://data.si/blog/kadrovske-evidence-obvezni-akti/" type="secondary" />
          <SourceLink label="Vzorci pogodb" url="https://www.vzorcipogodb.si/" type="secondary" />
        </div>
      </CardContent>
    </Card>
  );
}

function SourceLink({ label, url, type }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors hover:bg-slate-50 ${type === "primary" ? "border-indigo-200 bg-indigo-50/50" : "border-slate-200"}`}
    >
      <Shield className={`h-4 w-4 flex-shrink-0 ${type === "primary" ? "text-indigo-500" : "text-slate-400"}`} />
      <span className="text-sm">{label}</span>
    </a>
  );
}
